// Initializes Microsoft Clarity and the LinkedIn Insight Tag. Both are
// deferred until the page is idle (or first user interaction) so they
// don't block LCP/TBT on initial load. GA4 is loaded via the standard
// gtag snippet in index.html instead.
// Skipped in dev builds and when the corresponding env var is missing.

const LINKEDIN_PARTNER_ID = "9962273";

type IdleWindow = Window & {
  requestIdleCallback?: (
    cb: () => void,
    opts?: { timeout?: number },
  ) => number;
};

let initialized = false;
let scheduled = false;

export function initAnalytics(): void {
  if (scheduled) return;
  if (typeof window === "undefined") return;
  if (!import.meta.env.PROD) return;
  scheduled = true;

  const run = () => {
    if (initialized) return;
    initialized = true;

    const clarityId = import.meta.env.VITE_CLARITY_ID;
    if (clarityId) loadClarity(clarityId);

    loadLinkedInInsight(LINKEDIN_PARTNER_ID);
  };

  const onceAny = (events: (keyof WindowEventMap)[], handler: () => void) => {
    const wrapped = () => {
      events.forEach((e) => window.removeEventListener(e, wrapped));
      handler();
    };
    events.forEach((e) =>
      window.addEventListener(e, wrapped, { once: true, passive: true }),
    );
    return wrapped;
  };

  const schedule = () => {
    const idleWindow = window as IdleWindow;
    const ric =
      idleWindow.requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 1500));
    ric(run, { timeout: 4000 });
    onceAny(["pointerdown", "touchstart", "scroll", "keydown"], run);
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }
}

function loadClarity(projectId: string): void {
  (function (c: Window, l: Document, a: string, r: string) {
    c.clarity =
      c.clarity ||
      function (...args: unknown[]) {
        (c.clarity!.q = c.clarity!.q || []).push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + a;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode!.insertBefore(t, y);
  })(window, document, projectId, "script");
}

function loadLinkedInInsight(partnerId: string): void {
  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);

  if (!window.lintrk) {
    const lintrk = function (a: unknown, b: unknown) {
      lintrk.q!.push([a, b]);
    } as Window["lintrk"] & { q?: unknown[][] };
    lintrk.q = [];
    window.lintrk = lintrk;
  }

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  document.head.appendChild(s);
}
